import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DashboardLayout from '@/components/DashboardLayout';
import { tenantService, type TenantDto } from '@/services/tenants';
import { authService } from '@/services/auth';
import { Building2, CheckCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    authService.getSelectedTenantId()
  );
  const [newTenantName, setNewTenantName] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingTeacher, setInvitingTeacher] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      setTenants(data);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load tenants',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      await tenantService.switchTenant(tenantId);
      setSelectedTenantId(tenantId);
      toast({
        title: 'Success',
        description: 'Tenant switched successfully',
      });
      // Reload page to apply new tenant context
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to switch tenant',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTenant = async () => {
    const name = newTenantName.trim();
    if (!name) return;

    try {
      setCreatingTenant(true);
      const created = await tenantService.createTenant({ name });
      setNewTenantName('');
      setSelectedTenantId(created.id);
      await loadTenants();
      toast({
        title: 'Tenant created',
        description: `${created.name} was created successfully`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to create tenant',
        variant: 'destructive',
      });
    } finally {
      setCreatingTenant(false);
    }
  };

  const handleInviteTeacher = async () => {
    if (!selectedTenantId || !inviteEmail.trim()) return;

    try {
      setInvitingTeacher(true);
      const response = await authService.inviteTeacher(selectedTenantId, inviteEmail.trim());
      setInviteEmail('');
      setLastInviteLink(response.invitationUrl);
      toast({
        title: 'Teacher invited',
        description: `Invitation created for ${response.email}`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to invite teacher',
        variant: 'destructive',
      });
    } finally {
      setInvitingTeacher(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!lastInviteLink) return;

    try {
      await navigator.clipboard.writeText(lastInviteLink);
      toast({
        title: 'Copied',
        description: 'Invitation link copied to clipboard',
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please copy the invitation link manually',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tenants - Arrangelly Dashboard</title>
      </Helmet>

      <DashboardLayout>
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Tenant Management</h1>
            <p className="text-gray-400">Create tenants, select active context, and invite tenant teachers</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="glass-panel rounded-xl p-4">
              <p className="text-sm text-foreground mb-2">Create Tenant</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={newTenantName}
                  onChange={(event) => setNewTenantName(event.target.value)}
                  placeholder="New tenant name"
                  className="bg-slate-50 border-slate-200 text-foreground placeholder:text-gray-500"
                />
                <Button onClick={handleCreateTenant} disabled={!newTenantName.trim() || creatingTenant}>
                  {creatingTenant ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>

            <div className="glass-panel rounded-xl p-4">
              <p className="text-sm text-foreground mb-2">
                Invite Tenant Teacher {selectedTenantId ? '' : '(select a tenant first)'}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder="teacher@email.com"
                  className="bg-slate-50 border-slate-200 text-foreground placeholder:text-gray-500"
                  disabled={!selectedTenantId}
                />
                <Button
                  onClick={handleInviteTeacher}
                  disabled={!selectedTenantId || !inviteEmail.trim() || invitingTeacher}
                >
                  {invitingTeacher ? 'Inviting...' : 'Invite'}
                </Button>
              </div>
              {lastInviteLink && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-1">Latest invite link</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={lastInviteLink}
                      readOnly
                      className="bg-slate-100 border-slate-200 text-gray-300 text-xs sm:text-sm"
                    />
                    <Button variant="outline" className="border-white/20" onClick={handleCopyInviteLink}>
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {tenants.length === 0 ? (
            <div className="glass-panel rounded-xl p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">No tenants found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {tenants.map((tenant, index) => (
                <motion.div
                  key={tenant.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`glass-panel rounded-xl p-4 sm:p-6 ${
                    selectedTenantId === tenant.id
                      ? 'ring-2 ring-primary border-primary/50'
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-foreground">{tenant.name}</h3>
                        <p className="text-sm text-gray-400">Tenant ID: {tenant.id.slice(0, 8)}...</p>
                      </div>
                    </div>
                    {selectedTenantId === tenant.id && (
                      <CheckCircle className="w-5 h-5 text-primary" />
                    )}
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span>Created: {new Date(tenant.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleSwitchTenant(tenant.id)}
                    className={`w-full ${
                      selectedTenantId === tenant.id
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'bg-primary hover:bg-primary/90'
                    }`}
                    variant={selectedTenantId === tenant.id ? 'outline' : 'default'}
                  >
                    {selectedTenantId === tenant.id ? 'Currently Active' : 'Switch to Tenant'}
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default TenantsPage;

